import { useTranslation } from '@cobliteam/i18n';
import { Button, Tooltip } from '@cobliteam/ui/components';
import Input from '@cobliteam/ui/form/Input';
import { useDebounce } from '@cobliteam/ui/hooks';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { useEffect, useRef, useState } from 'react';
import AlertFooter from '../components/AlertFooter';
import { CommandDialog, CommandInput } from '../components/Command';
import DeviceDetails from '../components/Details/Device/DeviceDetails';
import DriverDetails from '../components/Details/Driver/DriverDetails';
import GeofenceDetails from '../components/Details/Geofence/GeofenceDetails';
import PageDetail from '../components/Details/Page/PageDetail';
import VehicleDetails from '../components/Details/Vehicle/VehicleDetails';
import VehicleGroupDetails from '../components/Details/VehicleGroup/VehicleGroupDetails';
import { SearchIcon } from '../components/SearchIcon';
import GlobalSearchSidebar from '../containers/GlobalSearchSidebar';
import { VirtualizedList } from '../containers/VirtualizedList';
import { trackGlobalSearchEvents } from '../helpers';
import { useKeyboardNavigation } from '../hooks/useKeyboardNavigation';
import useSearch from '../hooks/useSearch';
import { EnumCategory } from '../types';

interface GlobalSearchContentProps {
  handleUpdateData: () => void;
  isRefetching: boolean;
}
const isMac = /macintosh|mac os x/i.test(navigator.userAgent);

const GlobalSearchContent = ({ handleUpdateData, isRefetching }: GlobalSearchContentProps) => {
  const { t } = useTranslation('global-search');
  const searchRef = useRef<HTMLInputElement>(null);
  const [inputSearch, setInputSearch] = useState('');
  const debouncedSearch = useDebounce(inputSearch, 400);
  const [isHovered, setIsHovered] = useState(false);

  const { isOpen, selectedItem, setSearch, setIsOpen } = useSearch();

  // Add keyboard navigation
  useKeyboardNavigation();

  const handleEscapeKeyDown = (e: Event) => {
    e.preventDefault();
    setSearch('');
    setInputSearch('');
    setIsOpen(false);
  };

  const onHandleUpdateData = () => {
    handleUpdateData();
    trackGlobalSearchEvents.updateData();
  };

  useEffect(() => {
    if (isOpen) {
      setSearch(debouncedSearch);
      if (debouncedSearch) trackGlobalSearchEvents.search(debouncedSearch);
    }

    return () => {
      if (!isOpen) {
        setSearch('');
        setInputSearch('');
      }
    };
  }, [debouncedSearch, setSearch, isOpen]);

  return (
    <>
      <div className='relative'>
        <div data-visual-test='blackout' className='relative flex items-center gap-2 rounded-lg border border-gray-6'>
          <Input
            data-testid='global-search-input'
            className='h-[34px] w-[426px] justify-between border-none bg-transparent'
            loading={false}
            disabled={false}
            autoFocus={isOpen}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            startIcon={<SearchIcon isHovered={isHovered} />}
            placeholder='Procurar veículos, motoristas e etc...'
            onClick={() => {
              trackGlobalSearchEvents.openSearch();
              setIsOpen(true);
            }}
          />
          <kbd className='absolute right-2 inline-flex h-5 max-h-full items-center rounded border border-gray-5 bg-gray-2 px-1 font-[inherit] font-medium text-gray-a9 text-xs shadow-sm'>
            {isMac ? '⌘' : 'Ctrl'} + K
          </kbd>
        </div>
      </div>
      <CommandDialog size='lg' open={isOpen} onOpenChange={setIsOpen} shouldFilter={false} onEscapeKeyDown={handleEscapeKeyDown}>
        <div className='flex items-center justify-between gap-2 border-gray-4 border-b pr-2'>
          <CommandInput ref={searchRef} autoFocus disabled={isRefetching} placeholder={t('search.placeholder')} value={inputSearch} onValueChange={setInputSearch} />
          <Tooltip content={t('search.update_fleet_data')}>
            <Button variant='ghost' size='sm' onClick={onHandleUpdateData} className='p-2'>
              <Icon name='refresh-cw' className={cn(isRefetching && 'animate-spin')} />
            </Button>
          </Tooltip>
        </div>
        <div className='grid h-[500px] grid-cols-12'>
          <section className='col-span-3 border-gray-4 border-r'>
            <GlobalSearchSidebar />
          </section>
          <section className='col-span-4 overflow-auto border-gray-4 border-r'>
            <VirtualizedList />
          </section>
          <section className='col-span-5 flex gap-2'>
            {selectedItem?.category.id === EnumCategory.VEHICLES && <VehicleDetails />}
            {selectedItem?.category.id === EnumCategory.DRIVERS && <DriverDetails />}
            {selectedItem?.category.id === EnumCategory.DEVICES && <DeviceDetails />}
            {selectedItem?.category.id === EnumCategory.VEHICLE_GROUPS && <VehicleGroupDetails />}
            {selectedItem?.category.id === EnumCategory.GEOFENCES && <GeofenceDetails />}
            {selectedItem?.category.id === EnumCategory.PAGES && <PageDetail />}
          </section>
        </div>
        <AlertFooter />
      </CommandDialog>
    </>
  );
};

export default GlobalSearchContent;
